import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material';
import { NotificationService } from 'src/@core/services/notification.service';
import { AgentEvalsService } from '../agent-evals.service';
import { AgentEvalCriterion, AgentEvalTest } from '../agent-evals.types';

export interface AddTestDialogData {
  entityId: string;
  projectId: string;
  test?: AgentEvalTest;
}

@Component({
  selector: 'hour4u-admin-add-agent-eval-test-dialog',
  templateUrl: './add-test-dialog.component.html',
  styleUrls: ['./add-test-dialog.component.scss'],
})
export class AddAgentEvalTestDialogComponent {
  name = '';
  area = 'Call Quality';
  critical = false;
  description = '';
  criteria: AgentEvalCriterion[] = [{ label: '', weight: 100 }];
  saving = false;
  areas = [
    'Call Quality',
    'Outcomes',
    'Compliance',
    'Language',
    'Tasks',
    'Follow-ups',
    'Pricing',
    'General',
  ];

  constructor(
    private dialogRef: MatDialogRef<AddAgentEvalTestDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AddTestDialogData,
    private agentEvalsService: AgentEvalsService,
    private notify: NotificationService
  ) {
    if (data && data.test) {
      this.name = data.test.name || '';
      this.area = data.test.area || 'General';
      this.critical = !!data.test.critical;
      this.description = data.test.description || '';
      const criteria = (data.test.success_criteria || []).map((c) => ({
        label: c.label || '',
        weight: Number(c.weight) || 0,
      }));
      if (criteria.length) {
        this.criteria = criteria;
      }
    }
  }

  get isEdit(): boolean {
    return !!(this.data && this.data.test && this.data.test.test_id);
  }

  addCriterion(): void {
    this.criteria.push({ label: '', weight: 0 });
    this.redistributeWeights();
  }

  removeCriterion(index: number): void {
    if (this.criteria.length <= 1) {
      return;
    }
    this.criteria.splice(index, 1);
    this.redistributeWeights();
  }

  /** Split 100 evenly across criteria; remainder goes to the first row. */
  redistributeWeights(): void {
    const n = this.criteria.length;
    if (n <= 0) {
      return;
    }
    const base = Math.floor(100 / n);
    let rem = 100 - base * n;
    this.criteria.forEach((c, i) => {
      c.weight = base + (i < rem ? 1 : 0);
    });
  }

  weightTotal(): number {
    return this.criteria.reduce((sum, c) => sum + (Number(c.weight) || 0), 0);
  }

  save(): void {
    if (!this.name.trim()) {
      this.notify.showMessage('Test name is required', 3000);
      return;
    }
    const cleaned = this.criteria
      .map((c) => ({ label: (c.label || '').trim(), weight: Number(c.weight) || 0 }))
      .filter((c) => c.label);
    if (!cleaned.length) {
      this.notify.showMessage('Add at least one success criterion', 3000);
      return;
    }
    if (Math.abs(this.weightTotal() - 100) > 1) {
      this.notify.showMessage('Criterion weights must sum to 100', 3000);
      return;
    }
    this.saving = true;
    if (this.isEdit) {
      this.agentEvalsService
        .updateTest({
          entity_id: this.data.entityId,
          project_id: this.data.projectId,
          test_id: this.data.test.test_id,
          name: this.name.trim(),
          area: this.area,
          critical: this.critical,
          description: this.description,
          success_criteria: cleaned,
        })
        .subscribe(
          (test) => {
            this.saving = false;
            this.dialogRef.close(test);
          },
          (err) => {
            this.saving = false;
            const msg =
              (err && err.error && err.error.message) || 'Failed to update test';
            this.notify.showMessage(msg, 4000);
          }
        );
      return;
    }
    this.agentEvalsService
      .createTest({
        entity_id: this.data.entityId,
        project_id: this.data.projectId,
        name: this.name.trim(),
        area: this.area,
        critical: this.critical,
        description: this.description,
        success_criteria: cleaned,
        source: 'manual',
      })
      .subscribe(
        (test) => {
          this.saving = false;
          this.dialogRef.close(test);
        },
        (err) => {
          this.saving = false;
          const msg =
            (err && err.error && err.error.message) || 'Failed to create test';
          this.notify.showMessage(msg, 4000);
        }
      );
  }

  close(): void {
    this.dialogRef.close();
  }
}
